import { defineModule } from '@portal/kernel';

/** Channel revenue of the YouTube business (docs/design/P5_YOUTUBE_REVENUE.md). */
export const manifest = defineModule({
  key: 'youtube',
  version: '0.1.0',
  kind: 'business',
  coreVersion: '^0.1.0',
  name: { vi: 'YouTube', en: 'YouTube' },
  description: {
    vi: 'Kênh YouTube, doanh thu sau chia sẻ, mục tiêu và mốc tối thiểu của người quản lý kênh.',
    en: 'YouTube channels, revenue after sharing, targets and channel manager minimums.',
  },
  dependsOn: [
    'core-system',
    'core-audit',
    'core-identity',
    'core-organization',
    'core-access',
    'core-notification',
    'partners',
  ],
  database: { schema: 'youtube' },
  permissions: [
    {
      key: 'youtube.revenue.view',
      name: { vi: 'Xem kênh và doanh thu', en: 'View channels and revenue' },
      dataScopes: ['own', 'department', 'department_tree', 'tenant'],
    },
    {
      key: 'youtube.revenue.enter',
      name: {
        vi: 'Nhập doanh thu kênh mình quản lý',
        en: 'Enter revenue of own channels',
      },
    },
    {
      key: 'youtube.revenue.edit-any',
      name: {
        vi: 'Nhập và sửa doanh thu mọi kênh, không giới hạn số ngày',
        en: 'Enter and correct revenue of any channel, any day',
      },
    },
    {
      key: 'youtube.channel.manage',
      name: {
        vi: 'Quản lý kênh, người quản lý và tỷ lệ chia sẻ',
        en: 'Manage channels, managers and share rates',
      },
    },
    {
      key: 'youtube.settings.manage',
      name: { vi: 'Cài đặt mục tiêu và mốc tối thiểu', en: 'Manage targets and minimums' },
    },
    {
      key: 'youtube.month.close',
      name: { vi: 'Chốt và mở lại tháng', en: 'Close and reopen months' },
    },
    { key: 'youtube.report.export', name: { vi: 'Xuất báo cáo', en: 'Export reports' } },
    {
      key: 'youtube.milestone.manage',
      name: {
        vi: 'Đặt mốc doanh thu đầu quý và cài đặt thưởng mốc',
        en: 'Set quarterly revenue milestones and their bonuses',
      },
    },
  ],
  navigation: [
    {
      id: 'youtube',
      label: { vi: 'YouTube', en: 'YouTube' },
      icon: 'youtube',
      order: 100,
      children: [
        {
          id: 'youtube.overview',
          label: { vi: 'Tổng quan tháng', en: 'Monthly overview' },
          icon: 'layout-dashboard',
          path: '/youtube',
          permission: 'youtube.revenue.view',
          order: 1,
        },
        {
          id: 'youtube.entry',
          label: { vi: 'Nhập doanh thu', en: 'Enter revenue' },
          icon: 'pencil-line',
          path: '/youtube/entry',
          permission: 'youtube.revenue.enter',
          order: 2,
        },
        {
          id: 'youtube.daily',
          label: { vi: 'Doanh thu theo ngày', en: 'Daily revenue' },
          icon: 'calendar-days',
          path: '/youtube/daily',
          permission: 'youtube.revenue.view',
          order: 3,
        },
        {
          id: 'youtube.channels',
          label: { vi: 'Kênh', en: 'Channels' },
          icon: 'tv',
          path: '/youtube/channels',
          permission: 'youtube.revenue.view',
          order: 4,
        },
        {
          id: 'youtube.milestones',
          label: { vi: 'Mốc doanh thu', en: 'Revenue milestones' },
          icon: 'trophy',
          path: '/youtube/milestones',
          permission: 'youtube.revenue.view',
          order: 5,
        },
        {
          id: 'youtube.settings',
          label: { vi: 'Cài đặt & chốt tháng', en: 'Settings & closing' },
          icon: 'settings',
          path: '/youtube/settings',
          permission: 'youtube.settings.manage',
          order: 6,
        },
      ],
    },
  ],
  notifications: [
    {
      key: 'youtube.entry-missing',
      name: { vi: 'Nhắc nhập doanh thu', en: 'Revenue entry reminder' },
      description: { vi: '10:00 hằng ngày, khi kênh nhập tay chưa có số của hôm qua.' },
      defaultChannels: ['in_app', 'email'],
    },
    {
      key: 'youtube.minimum-behind',
      name: { vi: 'QLK chậm tiến độ mốc tối thiểu', en: 'Managers behind the minimum' },
      description: {
        vi: 'Thứ Hai hằng tuần và ngày 20, cho kế toán, ban giám đốc và trưởng đơn vị.',
      },
      defaultChannels: ['in_app', 'email'],
    },
    {
      key: 'youtube.sync-failed',
      name: { vi: 'Lỗi đồng bộ YouTube', en: 'YouTube sync failed' },
      description: { vi: 'Khi một kênh mất kết nối hoặc đồng bộ lỗi.' },
      defaultChannels: ['in_app', 'email'],
    },
    {
      key: 'youtube.month-summary',
      name: { vi: 'Tổng kết doanh thu tháng', en: 'Monthly revenue summary' },
      description: { vi: 'Ngày 3 hằng tháng, số liệu tháng trước.' },
      defaultChannels: ['in_app', 'email'],
    },
    {
      key: 'youtube.milestone-reached',
      name: { vi: 'Kênh đạt mốc doanh thu', en: 'Channel reached a revenue milestone' },
      description: { vi: 'Chúc mừng QLK và báo người duyệt mốc.' },
      defaultChannels: ['in_app', 'email'],
    },
    {
      key: 'youtube.milestone-pending',
      name: { vi: 'Mốc doanh thu cần duyệt / kiểm tra', en: 'Revenue milestone to review' },
      description: {
        vi: 'Cho người duyệt mốc: mốc mới được đề xuất, hoặc ngày đạt mốc bị sửa giảm.',
      },
      defaultChannels: ['in_app', 'email'],
    },
    {
      key: 'youtube.milestone-decided',
      name: { vi: 'Kết quả duyệt mốc doanh thu', en: 'Revenue milestone decided' },
      defaultChannels: ['in_app'],
    },
  ],
  widgets: [
    {
      key: 'youtube.month',
      title: { vi: 'Doanh thu YouTube tháng này', en: 'YouTube revenue this month' },
      permission: 'youtube.revenue.view',
      size: 'md',
      order: 10,
    },
    {
      key: 'youtube.managers',
      title: { vi: 'Tiến độ mốc tối thiểu', en: 'Progress to the minimum' },
      permission: 'youtube.revenue.view',
      order: 20,
    },
    {
      key: 'youtube.missing',
      title: { vi: 'Kênh chưa nhập số hôm qua', en: 'Channels missing yesterday' },
      permission: 'youtube.revenue.enter',
      order: 30,
    },
    {
      key: 'youtube.sync',
      title: { vi: 'Kết nối YouTube tự động', en: 'Automatic YouTube sync' },
      permission: 'youtube.channel.manage',
      order: 40,
    },
  ],
  roleTemplates: [
    {
      key: 'channel-manager',
      name: { vi: 'Quản lý kênh YouTube', en: 'YouTube channel manager' },
      description: { vi: 'Nhập và xem doanh thu các kênh mình quản lý.' },
      grants: [
        { permission: 'youtube.revenue.view', scope: 'own' },
        { permission: 'youtube.revenue.enter' },
      ],
    },
    {
      key: 'team-lead',
      name: { vi: 'Trưởng nhóm YouTube', en: 'YouTube team lead' },
      description: { vi: 'Xem doanh thu của đơn vị mình và các đơn vị cấp dưới.' },
      grants: [
        { permission: 'youtube.revenue.view', scope: 'department_tree' },
        { permission: 'youtube.report.export' },
      ],
    },
    {
      key: 'accountant',
      name: { vi: 'Kế toán YouTube', en: 'YouTube accountant' },
      description: {
        vi: 'Quản lý kênh, tỷ lệ chia sẻ, mục tiêu; sửa số liệu và chốt tháng.',
      },
      grants: [
        { permission: 'youtube.revenue.view', scope: 'tenant' },
        { permission: 'youtube.revenue.enter' },
        { permission: 'youtube.revenue.edit-any' },
        { permission: 'youtube.channel.manage' },
        { permission: 'youtube.settings.manage' },
        { permission: 'youtube.month.close' },
        { permission: 'youtube.report.export' },
        { permission: 'youtube.milestone.manage' },
      ],
    },
    {
      key: 'executive',
      name: { vi: 'Xem báo cáo YouTube toàn công ty', en: 'YouTube company-wide viewer' },
      grants: [
        { permission: 'youtube.revenue.view', scope: 'tenant' },
        { permission: 'youtube.report.export' },
      ],
    },
  ],
});
